import { Box, Checkbox, createStyles, IconButton, makeStyles, Typography } from "@material-ui/core";
import { Order, OrderItem } from "common-ts/dist/models/Orders";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useEffect, useState } from "react";
import { FareastButton } from "./OverlayComponentButton";

const useStyles = makeStyles(() =>
    createStyles({
        arrowBackButton: {
            color: "black",
            padding: "0 0 15px 0",
        },
        title: {
            fontWeight: 600,
            fontSize: "1.2rem",
        },
        productsContainer: {
            display: "flex",
            flexDirection: "column",
            marginTop: "15px",
        },
        productItem: {
            borderRadius: "15px",
            padding: "15px",
            marginBottom: "15px",
            display: "flex",
            alignItems: "stretch",
            gap: "10px",
        },
        productName: {
            fontSize: "0.9rem",
            fontWeight: 500,
        },
        productDetail: {
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            flex: 1,
            minHeight: 0,
        },
        productTextBox: {
            flex: 1,
            minWidth: 0,
        },
        sizeName: {
            fontSize: "0.75rem",
            color: "#8a8a8aff",
        },
        quantityBox: {
            display: "flex",
            alignItems: "center",
            border: "2px solid #9d9d9dff",
            padding: "2px 6px",
            alignSelf: "flex-end",
            marginTop: "auto",
            marginRight: '2px'
        },
        qtyText: {
            margin: "2px 8px 0px 8px",
            minWidth: "20px",
            textAlign: "center",
        },
        checkbox: {
            padding: 0,
            "&.Mui-checked": {
                color: "#000",
            },
        },
    })
);

interface Props {
    handleBack: () => void;
    handleNext: () => void;
    selectedOrder: Order;
    setSelectedProducts: (selectedProducts: OrderItem[]) => void;
}

export default function ProductSelect({ handleBack, handleNext, selectedOrder, setSelectedProducts }: Props) {
    const classes = useStyles();
    const ordersItem = selectedOrder.cartProductList;

    if (!ordersItem) {
        handleBack();
    }

    const getKey = (order: OrderItem) => {
        let sizeName = "";
        if (order.sizeType === "personal") {
            const meas = order.userMeasurementsForItem;
            if (typeof meas === "string") {
                sizeName = meas;
            } else {
                sizeName = meas.measurementName || "NOTSELECTED";
            }
        } else {
            sizeName = order.regularSize;
        }

        return `${order.productId}_${sizeName}`;
    };

    const [selectedItems, setSelectedItems] = useState<{ key: string; order: OrderItem; quantity: number }[]>([]);

    const isSelected = (currentOrder: OrderItem) => selectedItems.some((sel) => sel.key === getKey(currentOrder));

    const toggleSelectItem = (item: OrderItem) => {
        const key = getKey(item);
        setSelectedItems((prev) => {
            const exists = prev.find((sel) => sel.key === key);
            if (exists) {
                return prev.filter((sel) => sel.key !== key);
            } else {
                return [...prev, { key, order: item, quantity: 1 }];
            }
        });
    };

    const updateQuantity = (order: OrderItem, selectedAmount: number, maxAmount: number) => {
        const key = getKey(order);
        setSelectedItems((prev) =>
            prev.map((selected) => {
                if (selected.key === key) {
                    const newQty = Math.min(maxAmount, Math.max(1, selected.quantity + selectedAmount));
                    return { ...selected, quantity: newQty };
                }
                return selected;
            })
        );
    };

    function submit() {
        let selected: OrderItem[] = [];
        selectedItems.forEach((item) => {
            for (let i = 0; i < item.quantity; i++) {
                selected.push(item.order);
            }
        });
        setSelectedProducts(selected);
        handleNext();
    }

    return (
        <Box>
            <IconButton onClick={handleBack} className={classes.arrowBackButton}>
                <ArrowBackIcon />
            </IconButton>
            <Typography className={classes.title}>Pilih Produk Yang Bermasalah</Typography>
            <Box className={classes.productsContainer}>
                {ordersItem.map((item: OrderItem) => {
                    let sizeName = "";
                    if (item.sizeType === "personal") {
                        const meas = item.userMeasurementsForItem;
                        if (typeof meas === "string") {
                            sizeName = meas;
                        } else {
                            sizeName = meas.measurementName || "NOTSELECTED";
                        }
                    } else {
                        sizeName = item.regularSize;
                    }

                    return (
                        <Box
                            className={classes.productItem}
                            style={{
                                border: isSelected(item) ? "1px solid #727272" : "1px solid rgb(221,221,221)",
                            }}
                            onClick={() => toggleSelectItem(item)}
                        >
                            <IconButton style={{ alignSelf: "center", padding: "0px" }}>
                                <Checkbox checked={isSelected(item)} className={classes.checkbox} onChange={() => {}} />
                            </IconButton>
                            <img src={item.productDetail.displayImage} alt={`Gambar ${item.productDetail.name}`} style={{ width: "70px", marginTop: "auto", marginBottom: "auto" }} />

                            <Box className={classes.productDetail}>
                                <Box className={classes.productTextBox}>
                                    <Typography className={classes.productName}>{item.productDetail.name}</Typography>
                                    <Typography className={classes.sizeName}>{sizeName}</Typography>
                                </Box>
                                <Box className={classes.quantityBox}>
                                    <IconButton
                                        size="small"
                                        disabled={!isSelected(item)}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            updateQuantity(item, -1, item.amount);
                                        }}
                                    >
                                        <RemoveIcon fontSize="small" />
                                    </IconButton>
                                    <Typography className={classes.qtyText}>{selectedItems.find((sel) => sel.key === getKey(item))?.quantity || 1}</Typography>
                                    <IconButton
                                        size="small"
                                        disabled={!isSelected(item)}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            updateQuantity(item, 1, item.amount);
                                        }}
                                    >
                                        <AddIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
            <Box style={{ width: "100%", textAlign: "right" }}>
                <FareastButton
                    disabled={selectedItems.length === 0}
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                        submit();
                    }}
                    buttonMarginLeftRight="0px"
                    buttonMarginTopBottom="15px"
                >
                    LANJUT
                </FareastButton>
            </Box>
        </Box>
    );
}
